import {
  createLeaderboardApi,
  type LeaderboardDashboardApiItem,
} from '@e-learning/core';
import {
  useDebounce,
  type LeaderboardRow,
  type LeaderboardStats,
} from '@e-learning/shared';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { api } from '../../../../api/client';

export const LEADERBOARD_PAGE_SIZE = 10;

const leaderboardApi = createLeaderboardApi(api);

/** KPIs sem endpoint dedicado: média e engagement mantêm valores de referência até existir API. */
const STATIC_KPI_STATS: Pick<
  LeaderboardStats,
  'avgPointsPerUser' | 'coursesCompleted' | 'engagementRatePercent'
> = {
  avgPointsPerUser: 1847,
  coursesCompleted: 724,
  engagementRatePercent: 87,
};

function mapApiItemToRow(item: LeaderboardDashboardApiItem): LeaderboardRow {
  return {
    rank: item.rank,
    name: item.name,
    email: item.email,
    userId: '',
    totalPoints: item.totalPoints,
    coursesCompleted: item.coursesCompleted,
    lastActive: item.lastAccessDate,
  };
}

export function useLeaderboardDashboard() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useLayoutEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    leaderboardApi
      .fetchLeaderboardDashboard({
        page,
        limit: LEADERBOARD_PAGE_SIZE,
        search: debouncedSearch.trim() || undefined,
      })
      .then(res => {
        if (cancelled) return;
        setRows(res.data.map(mapApiItemToRow));
        setTotal(res.total);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch]);

  const stats: LeaderboardStats = useMemo(
    () => ({
      totalActiveUsers: total,
      ...STATIC_KPI_STATS,
    }),
    [total]
  );

  const onPageChange = useCallback((next: number) => {
    setPage(next);
  }, []);

  return {
    stats,
    rows,
    total,
    loading,
    page,
    pageSize: LEADERBOARD_PAGE_SIZE,
    onPageChange,
    search,
    setSearch,
  };
}
